/* eslint-disable @typescript-eslint/ban-ts-comment */
// all apis related to learnosity exams

import axios from 'axios';
import qs from 'qs';
import { ReportTreeNode } from '@components/Component/LearnosityExams/types';

// sessions API calls

export const fetchTestSessionTestlets = async (testSessionId: number) => {
  const url = `/api/learnosityexams/exam_session/${testSessionId}/testlets/`;
  return axios
    .get(url, {
      // @ts-ignore
      noCache: true,
    })
    .then((response) => ({ response }));
};

// use the existing api to get the test sessions for the section
// note that as of this writing, the test session rostering is limited to per section
export const fetchTestSessions = async (sectionId: string) => {
  const url = `/api/sections/${sectionId}/exams/`;

  return axios
    .get(url, {
      // @ts-ignore
      noCache: true,
    })
    .then((response) => ({ response }));
};
export const fetchLearnosityExamTestSessions = async (sectionId: string) => {
  const url = `/api/learnosityexams/section/${sectionId}/exams/`;

  return axios.get(url).then((response) => ({ response }));
};

export const fetchDistrictCurriculumExams = async (districtId: number, curriculumId: number) => {
  const url = `/api/districts/${districtId}/curriculums/${curriculumId}/exams/`;

  return axios.get(url).then((response) => ({ response }));
};

export const fetchTestletSessions = async (testletId: number) => {
  const url = `/api/learnosityexams/testlet/${testletId}/testletsessions/`;
  return axios
    .get(url, {
      // @ts-ignore
      noCache: true,
    })
    .then((response) => ({ response }));
};

export const lockTestlet = async (testletId: number, body?: any) => {
  const url = `/api/learnosityexams/testlet/${testletId}/lock/`;
  return axios.put(url, body).then((response) => ({ response }));
};

export const unlockTestlet = async (testletId: number, body?: any) => {
  const url = `/api/learnosityexams/testlet/${testletId}/unlock/`;
  return axios.put(url, body).then((response) => ({ response }));
};

export const createTestletSession = async (testletId: number, userId: number) => {
  const url = `/api/learnosityexams/testlet/${testletId}/testletsessions/`;
  return axios.post(url, { user_id: userId }).then((response) => ({ response }));
};

export const startTestletSession = async (testletId: number) => {
  const url = `/api/learnosityexams/testlet/${testletId}/start/`;
  return axios.post(url).then((response) => ({ response }));
};

export const completeTestletSession = async (testletId: number) => {
  const url = `/api/learnosityexams/testlet/${testletId}/complete/`;
  return axios.post(url).then((response) => ({ response }));
};

export const resetTestletSessions = async (testletId: number, studentIds: number[]) => {
  const url = `/api/learnosityexams/testlet/${testletId}/reset_progress/`;
  return axios.post(url, { student_ids: studentIds }).then((response) => ({ response }));
};

export const extendTestletSessionTime = async (
  testletId: number,
  studentIds: number[],
  extendDuration: number,
) => {
  const url = `/api/learnosityexams/testlet/${testletId}/extend_time/`;
  return axios
    .post(url, { student_ids: studentIds, extend_duration: extendDuration })
    .then((response) => ({ response }));
};

export const logTestletSessionActivity = async (
  testletSessionId: string,
  eventType: string,
  metadata: any,
) => {
  const url = `/api/learnosityexams/testletsessions/${testletSessionId}/activities`;
  return axios
    .post(url, { event_type: eventType, metadata })
    .then((response) => ({ response }));
};

export const fetchTestSessionCompletionStatus = async (courseSectionId: number) => {
  const url = `/api/learnosityexams/section/${courseSectionId}/completion_status/`;
  return axios.get(url).then((response) => ({ response }));
};

export const fetchSessionItemsTaken = async (testletSessionId: number) => {
  const url = `/api/learnosityexams/testletsessions/${testletSessionId}/taken/`;
  return axios.get(url).then((response) => ({ response }));
};

export const getStudentSections = async (testletId: number) => {
  const url = `/api/learnosityexams/testlet/${testletId}/student_sections/`;
  return axios.get(url).then((response) => ({ response }));
};


export type ReportTreeApiResponse = { response: { data: ReportTreeNode } };

/**
 * Fetches the report tree for a given report type and section.
 * Backend returns a single root node (ReportTreeNode) as the response body.
 */
export const getReportTree = async (
  reportType: 'skill' | 'standard' | 'lesson',
  sectionId: number,
  params: { curriculumsubject_id: number },
): Promise<ReportTreeApiResponse> => {
  const url = `/api/learnosityexams/reports/${reportType}/tree/section/${sectionId}/`;
  const { data } = await axios.get(url, { params });
  const tree = data as Partial<ReportTreeNode>;
  if (!tree) {
    throw new Error(
      `Invalid ${reportType} report tree response for section ${sectionId}`,
    );
  }
  return { response: { data: tree as ReportTreeNode } };
};

// Convenience wrappers for backward compatibility
export const getSkillTree = (
  sectionId: number,
  params: { curriculumsubject_id: number },
) => getReportTree('skill', sectionId, params);

export const getStandardTree = (
  sectionId: number,
  params: { curriculumsubject_id: number },
) => getReportTree('standard', sectionId, params);

export const getLessonTree = (
  sectionId: number,
  params: { curriculumsubject_id: number },
) => getReportTree('lesson', sectionId, params);

export const getLEReport = async (
  reportType: 'skill' | 'standard' | 'lesson',
  examId: number,
  params: { section_id: number; curriculumsubject_id: number[] },
) => {
  const url = `/api/learnosityexams/reports/${reportType}/exam/${examId}/`;
  return axios
    .get(url, {
      params,
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
    })
    .then((response) => ({ response }));
};

/**
 * Queue a CSV export of a section's exam report. The file is generated in the
 * background; the download URL arrives on the EXAM_REPORT_CSV_COMPLETED
 * websocket event.
 */
export const exportLEReportCSV = async (
  reportType: 'skill' | 'standard' | 'lesson',
  examId: number,
  sectionId: number,
): Promise<{ status: 'queued'; report_name: string }> => {
  const url = `/api/learnosityexams/reports/${reportType}/exam/${examId}/section/${sectionId}/export-csv/`;
  const response = await axios.post(url);
  return response.data;
};
